/**
 * NOT-001C2A · Per-tab collapse-state persistence for the
 * Operations Notification Centre filter panels.
 *
 * Pure helpers so a deterministic Jest test can exercise the
 * contract without booting React. localStorage keys are namespaced
 * per tab so switching tabs never mutates the "other" tab's stored
 * preference.
 */

const KEY_PREFIX = "notifCentre.filtersExpanded.";

function _key(tab) {
    return `${KEY_PREFIX}${tab}`;
}

/**
 * Read the persisted collapse state for `tab`. Default is
 * `expanded=true` on first visit — matches the previous behaviour.
 *
 * @param {"events"|"deliveries"} tab
 * @param {Storage} [storage=localStorage]
 * @returns {boolean}
 */
export function readCollapse(tab, storage) {
    const s = storage || (typeof localStorage !== "undefined" ? localStorage : null);
    if (!s) return true;
    try {
        const v = s.getItem(_key(tab));
        if (v === null) return true;
        return v === "1";
    } catch { return true; }
}

/**
 * Persist the collapse state for `tab` without touching any other
 * tab's stored preference.
 *
 * @param {"events"|"deliveries"} tab
 * @param {boolean} expanded
 * @param {Storage} [storage=localStorage]
 */
export function writeCollapse(tab, expanded, storage) {
    const s = storage || (typeof localStorage !== "undefined" ? localStorage : null);
    if (!s) return;
    try { s.setItem(_key(tab), expanded ? "1" : "0"); } catch {}
}

/**
 * Hydrate the initial `{events, deliveries}` state object used by
 * the page — reads each tab's preference independently so switching
 * back and forth restores the correct panel.
 *
 * @param {Storage} [storage]
 * @returns {{events:boolean, deliveries:boolean}}
 */
export function loadAllCollapse(storage) {
    return {
        events:      readCollapse("events", storage),
        deliveries:  readCollapse("deliveries", storage),
    };
}

// Exposed for tests.
export const _INTERNALS = { KEY_PREFIX };
